/**
 * Item catalogues implementation with react external store
 * NOTE: experimental feature for future use!
 *
 * @date 2024/08/15
 */
import * as _ from 'lodash-es';
import { useEffect, useSyncExternalStore } from 'react';

import helpIcon from '@/assets/icons/ic_outline-help.svg';
import { zoomifyBaseURL } from '@/config';
import { log, error } from '@/utils/logger';

import { fetchItemCatalogue } from '../api';
import {
  GO,
  Documents,
  FittingMeta,
  TKItemCatalog,
  CATALOGUE_TYPE as CTLG,
  CATALOGUE_CATEGORY as CTCT,
  LENGTH_TYPE as LEN,
  SPECIAL_JOB_ITEM as SJI,
  checkDuctRoundOnly,
  HELP_ICON,
  WIDTH_ICON,
  NOTE_ICON_FILE,
  missingIcons,
  LENGTH_FIELD,
  LENGTH_STR_FIELD,
  FIXTUREATTRS as FXT,
  FIELD_HAS_INSULATION,
} from '../types';

/**
 * check item type if has insulation field defined
 * @param itemID item type or entity type
 * @returns boolean
 */
export const hasInsulationDefined = (itemID: string) => {
  const insulation = findItemPropertyBy(itemID, FIELD_HAS_INSULATION);
  return !!insulation;
};

/**
 * invisible items from menu list, that means no need to filter out, to add new!
 * @2025/03/13
 */
const ITEM_TYPE_HIDDEN = [
  'DIRECTION_OF_FLOW', // @2025/01/16
  'SHUT_OFF_VALVE', // @2025/01/16
  'ENDPOINT_TYPE_C', // @2025/03/13
  'ENDPOINT_TYPE_D', // @2025/03/13
  'PIPE_ELBOW_TYPE_1', // @2025/03/13
  'END_CAP', // @2026/02/20
];

/**
 * Check if the item type to show or not
 * @param item item type to show or not
 * @returns true to show, false to hide
 */
const hiddenItemsFilter = (item: string) => !ITEM_TYPE_HIDDEN.includes(item);

// and more catalogues...

type CatalougeState = {
  cataloguesAvailable: boolean;
  /** all the catalogues */
  all: FittingMeta[];
  fittings: FittingMeta[];
  lengths: FittingMeta[];
  plumings: FittingMeta[];
};

// ============ START OF PRIVATE SECTION FOR CATALOGUE STORE ===============

/**
 * snapshot of catalouges state
 */
let initialCatalogueState: CatalougeState = {
  cataloguesAvailable: false,
  all: [],
  fittings: [],
  lengths: [],
  plumings: [],
};

/**
 * callback functions would dynamically change
 */
let listeners: (() => void)[] = [];

const emitChange = () => {
  for (const listener of listeners) {
    listener();
  }
};

// ============ END of PRIVATE SECTION FOR CATALOGUE STORE ===============
/**
 * Check selected symbol whether or not can switch shape in batch panel
 * @param entityType item defined in catalogue
 * @returns true or false
 */
export const checkShapeSwitchable = (entityType: string) => {
  const { all } = initialCatalogueState;
  const itemDef = all.find((item) => item.type === entityType);
  if (!itemDef) return false;
  // if shape property exists:
  return !!itemDef.shape;
};

/**
 * Find item icon from global items store
 * @2024/11/26
 * @param itemType symbol item type, such as `ELBOW`
 * @returns icon file path
 */
export const iconFinderXternal = (itemType: string | undefined) => {
  if (!itemType) return HELP_ICON;
  const { all, lengths } = initialCatalogueState;
  const capitalize = itemType.toUpperCase();

  // ! check note symbol icon - @2025/10/08
  if (capitalize == SJI.TBX) {
    return NOTE_ICON_FILE;
  }
  const length = lengths.find((it) => it.type === capitalize);
  if (length) {
    return WIDTH_ICON;
  }
  const commonItem = all.find((item) => item.type === capitalize);
  if (!commonItem?.icon) {
    // console.warn(`>>> could not find icon for: ${capitalize}`);
    return missingIcons[capitalize] || HELP_ICON;
  }
  return commonItem.icon;
};

/**
 * Find icon defined in item catalogue, if not found, use help icon instead
 * @param itemType
 * @returns
 */
export const iconFinder = iconFinderXternal;

/**
 * Find the custom item which have length field to disable its reszing in front-end.
 * @date 2025/05/27
 * @param itemID item id like: `CENTER_LINE_REDUCER_HYD`
 * @returns
 */
export const isCustomItemHaveLength = (itemID: string) => {
  const { all } = initialCatalogueState;
  const itemDef = all.find((item) => item.type === itemID);
  if (!itemDef) return false;
  const { fields, rawType } = itemDef;
  const haveLengthField = fields.includes(LENGTH_STR_FIELD);
  // ! a special kind of item to use visual `width` as length in phsical!
  // 2025/06/10
  const isFitting = rawType == CTLG.F;
  return haveLengthField && isFitting;
};

/**
 * get item definition with type
 * @param type item id with capitalized format
 * @returns
 */
export const findItemByType = (type: string) => {
  const catalogues = initialCatalogueState;
  return catalogues.all.find((item) => item.type === type);
};

/**
 * General function to get item properties defined in backend, `system` field included!
 *
 * @param type item id
 * @returns props fields list
 */
export const findPropsByType = (itemType: string) => {
  const catalogues = initialCatalogueState;
  const item = catalogues.all.find((item) => item.type === itemType);
  return item ? item.props : [];
};

/**
 *
 * NO `shape` & NO `system` in result!!
 *
 * @param type item(entity) type
 * @returns
 */
export const findFieldsByType = (itemType: string) => {
  const catalogues = initialCatalogueState;
  const item = catalogues.all.find((item) => item.type === itemType);
  return item ? item.fields : [];
};

/**
 * compose empty detail for replaced item from side panel
 * @param itemType
 * @param excludeLength if need length_?? field
 * @returns
 */
export const composeEmptyPropsForType = (
  itemType: string,
  excludeLength = false,
) => {
  const fields = findPropsByType(itemType);
  const detail = fields.reduce((last: GO, field: string) => {
    if (excludeLength && field.includes('length')) return last;
    last[field] = '';
    return last;
  }, {} as GO);
  return detail;
};

/**
 * Find safe fields to correct and save, NO `length_` fields!
 * == check all the dimension fields and omit unnecessary fields ==
 *
 * ! FIXME: must ignore the length field: `"length_unit_str"`
 * @2024/02/14
 * ! FIXME: Also need to filter out `length_1`, otherwise the `length_unit_str` will be erased!
 * ! https://taksoai.atlassian.net/browse/TAK-883
 * @2025/07/15
 * @param type item(entity) type, like `ELBOW`
 * @returns safe fields to edit and patch
 */
export const findFieldsToEdit = (type: string) => {
  const catalogues = initialCatalogueState;
  const item = catalogues.all.find((item) => item.type === type);
  const allFields = item ? item.fields : [];
  return allFields
    .filter((f) => f !== LENGTH_STR_FIELD)
    .filter((f) => f !== LENGTH_FIELD);
};

/**
 * Get field definition of one item type
 *
 * @param type item type
 * @param field field name
 * @returns
 */
export const findItemPropertyBy = (type: string, field: string) => {
  const catalogues = initialCatalogueState;
  const item = catalogues.all.find((item) => item.type === type);
  if (!item) return null;
  const property = item.properties.find((p) => p.name === field);
  if (!property) return null;
  return property;
};

export const findRawProperties = (type: string) => {
  const catalogues = initialCatalogueState;
  const item = catalogues.all.find((item) => item.type === type);
  return item ? item.properties : [];
};

/**
 * Get fields of FITTING item, no `system`, no `shape` included
 * @param type FITTING symbol type
 * @param checkSystem if include `system` field, false by default
 * @param checkShape if include `shape` field, false by default
 * @returns fields array
 */
export const getFittingFields = (
  type: string,
  checkSystem?: boolean,
  checkShape?: boolean,
) => {
  const catalogues = initialCatalogueState;
  const fitting = catalogues.fittings.find((item) => item.type === type);
  const fields = fitting ? fitting.fields : [];
  const hasShape = fitting ? fitting.shape : false;
  if (!checkSystem) {
    if (checkShape && hasShape) {
      return ['shape', ...fields];
    }
    return fields;
  }
  const withSystem = fitting ? !!fitting.hasSysem : false;
  return withSystem ? [...fields, 'system'] : fields;
};

/**
 * Get fields of LENGTH item, no `system`, no `shape` included
 * @param type LENGTH symbol type, DUCT_LENGTH, PIPE_LENGTH, FLEX
 * @param checkShape if include `shape`, false by default
 * @returns fields array
 */
export const getLengthFields = (type: string, checkShape?: boolean) => {
  const catalogues = initialCatalogueState;
  const lengths = catalogues.lengths.find((item) => item.type === type);
  if (!lengths) return [];
  const fields = lengths.fields;
  return checkShape ? ['shape', ...fields] : fields;
};

/**
 * Check `shape` existence for current fitting
 *
 * FIXME: check if round only, then does not need shape select in new duct dialog
 * @date 2024/03/21
 *
 * @param ductType duct type with snake case format
 * @returns
 */
export const shapeFinder = (ductType: string | undefined) => {
  if (!ductType) return false;
  const capitalize = ductType.toUpperCase();
  // thats rectangular, so need shape
  if (capitalize === SJI.RECT_DUCT) return true;

  // ! for `RRT_`s, no shape option needed:
  // @2025/07/03
  if (capitalize.includes(SJI.RRT)) return false;

  // NO need to toggle shape select!
  const isRoundOnly = checkDuctRoundOnly(ductType);
  if (isRoundOnly) return false;

  // ! checking ducts(have `shape`) catalogue store:
  // @2025/07/03
  const duct = getDucts().find((duct) => duct.type === capitalize);
  return duct ? !!duct.shape : false;
};

export const getDucts = () => {
  const catalogues = initialCatalogueState;
  const ducts = catalogues.all.filter((item) => item.category === CTCT.D);
  return ducts;
};

/**
 * need to control duct visibility...
 * @param hideInVisible
 * @returns
 */
export const getDuctFittings = (hideInVisible = false) => {
  const catalogues = initialCatalogueState;
  if (!catalogues.fittings.length) return [];
  const fittings = catalogues.fittings.filter(
    (item) => item.category === CTCT.D,
  );
  if (hideInVisible) {
    const visibleItem = (item: FittingMeta) => !!item.uiVisible;
    return fittings.filter(
      (item) => hiddenItemsFilter(item.type) && visibleItem(item),
    );
  }
  return fittings;
};

/**
 * need to control pipe visibility...
 * @param hideInVisible
 * @returns
 */
export const getPipeFittings = (hideInVisible = false) => {
  const catalogues = initialCatalogueState;
  if (!catalogues.fittings.length) return [];
  const pfilter = (item: FittingMeta) => item.category === CTCT.P;
  const pfits = catalogues.fittings.filter(pfilter);
  if (hideInVisible) {
    return pfits.filter((item) => hiddenItemsFilter(item.type));
  }
  return pfits;
};

export const isPipeFitting = (entityType: string) => {
  const pps = getPipeFittings();
  const result = pps.find((pp) => pp.type == entityType);
  return !!result;
};

/**
 * need to control plumings visibility...
 * @param hideInVisible
 * @returns
 */
export const getPlumbings = (hideInVisible = false) => {
  const plumings = initialCatalogueState.plumings;
  if (hideInVisible) {
    return plumings.filter((item) => hiddenItemsFilter(item.type));
  }
  return plumings;
};

/**
 * TODO: move to `useCatalogueStore`
 * Find out common fields of different fittings
 *
 * TWO cases for this function:
 *
 * - `useExpendComnFields` hook doese not need str length field.
 * - `getCommonFields4Table` to figure out the columns of table, it does need str length field.
 *
 * So, add one more optional parameter `ignoreLength`, true by default, but false in the 2nd case.
 *
 * @date 2024/03/14
 *
 * @param types entity types
 * @param ignoreLength if need length to display
 */
export const getCommonFieldsOfFittings = (
  types: string[],
  ignoreLength = true,
) => {
  // Validate step : null, may need other filter...
  const validTypes = types.filter((type) => !!type);

  const allFieldsByType: string[][] = [];
  const allFieldsInSet = new Set<string>();
  const checkExistanceInAll = (field: string) => {
    let existing = true;
    allFieldsByType.forEach((fields) => {
      if (!fields.includes(field)) return (existing = false);
    });
    return existing;
  };
  // step 1: get all the fields of all the types
  validTypes.forEach((type) => {
    // TODO: use the function in store:
    const fields = findPropsByType(type);
    // console.log(fields);
    fields.forEach((f) => allFieldsInSet.add(f));
    allFieldsByType.push(fields);
  });
  // step 2: find the common fields across all the types
  const commonFields: string[] = [];
  allFieldsInSet.forEach((f) => {
    const isCommon = checkExistanceInAll(f);
    isCommon && commonFields.push(f);
  });

  // FIXME: remove `length_1` & `length_unit_str` field if its in common fields
  // @2024/02/21
  const lengthFieldPos = commonFields.indexOf(LENGTH_FIELD);
  if (lengthFieldPos != -1) {
    commonFields.splice(lengthFieldPos, 1);
  }
  const lengthStrFieldPos = commonFields.indexOf(LENGTH_STR_FIELD);
  if (lengthStrFieldPos != -1 && ignoreLength) {
    commonFields.splice(lengthStrFieldPos, 1);
  }

  return commonFields;
};

/**
 * Convert item catalouge definition to item meta data
 * @param item Item catalogue definition
 * @returns FittingMeta(Pipe) data
 */
const itemCatalogueToFittingMeta = (item: TKItemCatalog): FittingMeta => {
  const fieldsNoSystem = item.properties
    .map((prop) => prop.name)
    .filter((field) => field !== 'system');
  const fieldsNoShape = fieldsNoSystem.filter((field) => field !== 'shape');
  const fittingMeta: FittingMeta = {
    rawType: item.type, // @2024/07/02
    category: item.category,
    type: item.id,
    label: item.description,
    icon: item.s3_icon_path
      ? `${zoomifyBaseURL}${item.s3_icon_path}`
      : missingIcons[item.id] || HELP_ICON,
    fields: fieldsNoShape,
    props: item.properties.map((prop) => prop.name),
    hasSysem: !!item.properties.find((prop) => prop.name === 'system'),
    properties: item.properties,
    // ! with this property to do some front-end control - @2025/05/27
    isCustom: item.classification_type == 'CUSTOM',
    /** added to control visibility - @2026/02/20 */
    uiVisible: item.ui_visible,
  };
  const hasShape = fieldsNoSystem.includes('shape');
  const hasDiameter = fieldsNoSystem.includes(FXT.D1);
  const hasWidth = fieldsNoSystem.includes(FXT.W1);
  const allowShapeSwitch = hasDiameter && hasWidth;
  // ! means it could switch shape
  // @2025/06/11
  if (hasShape || allowShapeSwitch) {
    fittingMeta.shape = true;
  }
  return fittingMeta;
};

/**
 * catalogue operations interface exposed to whole app
 */
export const itemCatalogueStore = {
  /**
   * ============== WRITE CATALOGUES METHOD =============
   * @param catalogues write catalouge to store
   */
  fillItemCataluges(catalogues: TKItemCatalog[]) {
    const { cataloguesAvailable } = initialCatalogueState;
    // prevent unnecessary reinit state!
    if (cataloguesAvailable) return;
    // clean up first
    const all = catalogues.map((catalogue) =>
      itemCatalogueToFittingMeta(catalogue),
    );
    // console.log(`## got catalogues in total: ${all.length}`);
    const fittings = all.filter((item) => item.rawType === CTLG.F);
    const lengths = all.filter((item) => item.rawType === CTLG.L);
    const plumings = all.filter((item) => item.category === CTCT.PLM);
    // == build a new state: ===
    initialCatalogueState = {
      cataloguesAvailable: true,
      all,
      fittings,
      lengths,
      plumings,
    };
    // == trigger react update ==
    emitChange();
  },
  // ======== separation line ========
  // == inline method ==
  subscribe(listener: () => void) {
    listeners = [...listeners, listener];
    return () => {
      // remove the newly added listener to achieve unsubscribe
      listeners = listeners.filter((l) => l !== listener);
    };
  },
  /**
   * exposed state change handler for `useSyncExternalStore`
   * @returns an immutable catalogues store state
   */
  getSnapshot() {
    return initialCatalogueState;
  },
};
/**
 * Global symbol item definition store
 * @returns cataglogue items
 */
export const useCatalogueStore = (companyId?: number) => {
  const { subscribe, getSnapshot } = itemCatalogueStore;
  const catalogues = useSyncExternalStore(subscribe, getSnapshot);

  // === Common method to find fields of a specific item type ===

  const findIconByType = (type: string) => {
    const item = catalogues.all.find((item) => item.type === type);
    return item ? item.icon : helpIcon;
  };

  useEffect(() => {
    if (initialCatalogueState.cataloguesAvailable) {
      // log(`## catalogues already loaded!`);
      return;
    }
    const loadItems = async () => {
      try {
        const response = await fetchItemCatalogue(companyId);
        const { documents } = response.data as Documents;
        const catalogues = documents as TKItemCatalog[];
        // console.log(documents);
        // 1. cache first
        itemCatalogueStore.fillItemCataluges(catalogues);
        // 2. rerender the UI dropdown list
        // log('## got the item catalogues defintion!');
      } catch (errInfo: any) {
        error(`### loading item catalouges having an error or timeout!`);
        log(errInfo);
        // toast.warn(ITEM_CATALOGUES_LOAD_FAILED);
      }
    };
    loadItems();
  }, [companyId]);

  return {
    ...catalogues,
    getPipeFittings,
    findIconByType,
  };
};
